/**
 * Resolves the careers web app origin from the current page.
 *
 * Rules:
 * - Optional REACT_APP_CAREERS_SITE_URL: full origin override when inference is not enough.
 * - Already on a careers.* host: same origin.
 * - localhost / 127.0.0.1 / *.localhost: only when REACT_APP_CAREERS_PORT is set to a
 *   different port than this page (the jobs app). Same port as this CRA site would
 *   just reload the marketing homepage on careers.localhost.
 * - Otherwise: protocol + "//careers." + a base host derived from the current hostname (strips leading www., then uses the last two DNS labels so staging.amglobal.dev → careers.amglobal.dev).
 * - If origin cannot be inferred (local without CAREERS_PORT, bare IP): public careers site.
 *
 * Multi-part public suffixes (e.g. example.co.uk) are not handled; set REACT_APP_CAREERS_SITE_URL for those.
 */
export const FALLBACK_PUBLIC_CAREERS_ORIGIN = "https://careers.amglobal.co";

function baseHostForCareers(hostname) {
  const withoutWww = hostname.replace(/^www\./i, "");
  const parts = withoutWww.split(".").filter(Boolean);
  if (parts.length <= 2) {
    return withoutWww;
  }
  return parts.slice(-2).join(".");
}

export function getCareersSiteOrigin() {
  const fromEnv = process.env.REACT_APP_CAREERS_SITE_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim().replace(/\/+$/, "");
  }

  if (typeof window === "undefined") {
    return FALLBACK_PUBLIC_CAREERS_ORIGIN;
  }

  const { protocol, hostname, port } = window.location;

  if (!hostname) {
    return FALLBACK_PUBLIC_CAREERS_ORIGIN;
  }

  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return FALLBACK_PUBLIC_CAREERS_ORIGIN;
  }

  const isLocal =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".localhost");

  if (isLocal) {
    const careersPort = process.env.REACT_APP_CAREERS_PORT;
    if (careersPort && String(careersPort) !== String(port || "")) {
      return `${protocol}//careers.localhost:${careersPort}`;
    }
    return FALLBACK_PUBLIC_CAREERS_ORIGIN;
  }

  if (/^careers\./i.test(hostname)) {
    const portSuffix = port ? `:${port}` : "";
    return `${protocol}//${hostname}${portSuffix}`;
  }

  const base = baseHostForCareers(hostname);
  return `${protocol}//careers.${base}`;
}

export function goToCareersSite() {
  if (typeof window === "undefined") {
    return;
  }
  const careersOrigin = getCareersSiteOrigin() || FALLBACK_PUBLIC_CAREERS_ORIGIN;
  window.location.assign(`${careersOrigin.replace(/\/+$/, "")}/`);
}
